import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Users, Clock, ThumbsUp, MapPin, Star } from 'lucide-react';

function BusinessInfo() {
  const stats = [
    {
      id: 1,
      icon: <Award className="w-8 h-8 text-brand-amber" />,
      number: '25+',
      label: 'Years of excellence',
      description: 'Serving Mumbai with quality signage',
    },
    {
      id: 2,
      icon: <Users className="w-8 h-8 text-brand-amber" />,
      number: '4.7',
      label: 'Google rating',
      description: 'Trusted by businesses across Mumbai',
    },
    {
      id: 3,
      icon: <Clock className="w-8 h-8 text-brand-amber" />,
      number: '24/7',
      label: 'Support available',
      description: 'Round the clock customer service',
    },
    {
      id: 4,
      icon: <ThumbsUp className="w-8 h-8 text-brand-amber" />,
      number: '5000+',
      label: 'Projects completed',
      description: 'From small shops to corporate brands',
    },
  ];

  const features = [
    {
      id: 1,
      title: 'Premium quality',
      description: 'High-grade materials and precision craftsmanship',
    },
    {
      id: 2,
      title: 'Custom designs',
      description: 'Tailored solutions for your brand identity',
    },
    {
      id: 3,
      title: 'Timely delivery',
      description: 'On-time project completion guaranteed',
    },
    {
      id: 4,
      title: 'Justified cost',
      description: 'Factory-direct value without compromising finish',
    },
  ];

  return (
    <section className="relative py-16 md:py-24 bg-surface overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.4] pointer-events-none bg-[radial-gradient(ellipse_100%_80%_at_50%_0%,rgba(234,88,12,0.12),transparent_55%)]"
        aria-hidden
      />
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <p className="text-brand-amber text-sm uppercase tracking-[0.18em] font-semibold mb-3">
            Why brands trust us
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            Values first,
            <br />
            <span className="text-gradient-brand">money follows</span>
          </h2>
          <p className="text-gray-400 text-base md:text-lg">
            In-house design and manufacturing mean sharper details, honest pricing, and
            faster delivery—so your brand looks as good on the facade as it does in the mockup.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-16">
          <article className="rounded-2xl bg-surface-elevated/80 border border-white/10 p-8 md:p-10 hover:border-brand-amber/25 transition-colors">
            <h3 className="text-3xl md:text-4xl font-bold text-gradient-brand mb-4">
              Quality
            </h3>
            <p className="text-gray-400 leading-relaxed mb-4">
              Years on the shop floor taught us precision matters—from LED uniformity to paint
              finish and weather sealing. We prototype, fabricate, and install with the same
              team, so nothing gets lost between departments.
            </p>
            <p className="text-gray-500 text-sm">
              Visit our experience centre for live samples, or browse the catalog to shortlist
              finishes before you commit.
            </p>
          </article>

          <article className="rounded-2xl bg-surface-elevated/80 border border-white/10 p-8 md:p-10 hover:border-brand-amber/25 transition-colors">
            <h3 className="text-3xl md:text-4xl font-bold text-gradient-brand mb-4">
              Justified cost
            </h3>
            <p className="text-gray-400 leading-relaxed mb-4">
              An owned manufacturing setup cuts middlemen and keeps budgets on the brief. Bulk
              material purchases and smart production scheduling help us stay competitive
              without cutting corners on materials.
            </p>
            <p className="text-gray-500 text-sm">
              Ask for a line-item estimate—clear specs, clear quantities, no surprise add-ons.
            </p>
          </article>
        </div>

        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-brand-orange/10 via-surface-elevated to-brand-yellow/5 p-8 md:p-10 mb-16 text-center md:text-left">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <p className="text-brand-yellow font-semibold text-sm uppercase tracking-wider mb-2">
                What customers think
              </p>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                Real projects. Real feedback.
              </h3>
              <p className="text-gray-400 max-w-xl">
                Most clients find us through referrals and repeat orders. Check our project gallery,
                then read reviews on Google to see how we handle timelines and finishes.
              </p>
              <div className="flex items-center gap-2 mt-4 justify-center md:justify-start">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className="w-5 h-5 fill-brand-yellow text-brand-yellow" aria-hidden />
                ))}
                <span className="text-gray-400 text-sm ml-2">4.7 on Google</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0 justify-center">
              <Link
                to="/projects"
                className="inline-flex items-center justify-center rounded-full bg-gradient-brand px-6 py-3 text-sm font-bold text-black hover:opacity-95"
              >
                View real projects
              </Link>
              <a
                href="https://www.google.com/search?q=Shyam+Advertising+Kandivali+reviews"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-full border-2 border-brand-amber/50 text-brand-amber px-6 py-3 text-sm font-semibold hover:bg-brand-amber/10"
              >
                Google reviews
              </a>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
          {stats.map((stat) => (
            <div
              key={stat.id}
              className="text-center p-6 rounded-2xl border border-white/10 bg-surface-elevated/60 hover:border-brand-amber/30 transition-all duration-300"
            >
              <div className="flex justify-center mb-3">{stat.icon}</div>
              <div className="text-3xl md:text-4xl font-bold text-white">{stat.number}</div>
              <div className="text-brand-amber font-semibold mt-1">{stat.label}</div>
              <div className="text-gray-500 text-sm mt-2">{stat.description}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          {features.map((feature) => (
            <div
              key={feature.id}
              className="flex items-start gap-3 p-4 rounded-xl hover:bg-white/[0.03] transition-colors"
            >
              <div className="w-1 h-full min-h-[3rem] shrink-0 rounded-full bg-gradient-to-b from-brand-orange to-brand-yellow" />
              <div>
                <h3 className="text-white font-semibold mb-1">{feature.title}</h3>
                <p className="text-gray-500 text-sm">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <div className="inline-flex items-center gap-2 flex-wrap justify-center bg-surface-elevated/90 px-6 py-3.5 rounded-full border border-white/10">
            <MapPin className="w-5 h-5 text-brand-amber shrink-0" />
            <span className="text-gray-400 text-sm text-left">
              Based in{' '}
              <span className="text-gradient-brand font-semibold">Kandivali East, Mumbai</span>
              {' '}— serving the entire Mumbai Metropolitan Region
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default BusinessInfo;
